import { createSlice } from '@reduxjs/toolkit'

/**
 * NAVIGATION CONTEXT SLICE
 *
 * Stores breadcrumb navigation context when navigating from FolderBrowser or Library
 * to a scenario detail page. This enables the HeaderSection to display the full
 * navigation hierarchy (Project > Release/Library > Folders > Scenario).
 *
 * Data is set when clicking a scenario in FolderBrowser or Library, and used
 * by HeaderSection to render Atlaskit Breadcrumbs with proper navigation.
 */

const initialState = {
  // true for library scenarios, false for release scenarios
  isLibrary: false,
  // { id, name } - only populated for release scenarios
  currentRelease: null,
  // Array of folder objects from root to current: [{ id, name }, ...]
  folderPath: []
}

const navigationContextSlice = createSlice({
  name: 'navigationContext',
  initialState,
  reducers: {
    /**
     * SET NAVIGATION CONTEXT
     *
     * Called before navigating to a scenario detail page.
     * Stores the breadcrumb path for display in HeaderSection.
     *
     * @param {object} payload
     * @param {boolean} payload.isLibrary - Whether navigating to a library scenario
     * @param {object|null} payload.currentRelease - Release info { id, name } for release scenarios
     * @param {Array} payload.folderPath - Array of folder objects from root to leaf
     */
    setNavigationContext: (state, action) => {
      state.isLibrary = action.payload.isLibrary || false
      state.currentRelease = action.payload.currentRelease || null
      state.folderPath = action.payload.folderPath || []
    },

    /**
     * CLEAR NAVIGATION CONTEXT
     *
     * Resets state to initial values.
     * Called when navigating away from scenario detail pages.
     */
    clearNavigationContext: () => initialState
  }
})

// Export actions
export const { setNavigationContext, clearNavigationContext } = navigationContextSlice.actions

// Export selector
export const selectNavigationContext = (state) => state.navigationContext

// Export reducer
export default navigationContextSlice.reducer
